import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientAllergy } from '@/types/patient-chart-api';

/**
 * Patient allergies list — Chart shell inner-tab "Allergies" (PROD-736 PR A).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:587`):
 *
 *   GET /api/Patients/GetPatientAllergyDetails?PatientId={id}
 *       → PatientAllergy[]
 *
 * The list ships as a flat array (not paged) — Flutter likewise renders
 * it as a single scrollable list. Active records have `isDeleted: false`;
 * deleted records remain in the response (tombstones) so the UI filters
 * them out client-side.
 *
 * Cache: keyed on `patientId`. Invalidated by the create / update / delete
 * mutations (those ship in PR B).
 */

export function usePatientAllergies(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-allergies', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ PatientId: patientId ?? '' });
      return get<PatientAllergy[]>(
        `/api/Patients/GetPatientAllergyDetails?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}
